import React, { useState } from 'react';
import { companyAPI } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const industries = [
  'Technology',
  'Healthcare',
  'Finance',
  'Retail',
  'Manufacturing',
  'Real Estate',
  'Construction',
  'Transportation',
  'Education',
  'Entertainment',
  'Hospitality',
  'Agriculture',
  'Energy',
  'Telecommunications',
  'Other'
];

const currencies = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'INR', name: 'Indian Rupee' }
];

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const AddCompanyModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    fiscal_year_end: 'December',
    currency: 'USD',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.industry) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      await companyAPI.create(formData);
      setFormData({
        name: '',
        industry: '',
        fiscal_year_end: 'December',
        currency: 'USD',
        description: ''
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-[#1A3C34]">Add New Company</DialogTitle>
          <DialogDescription className="text-[#1A3C34]/60">
            Enter the company details to start tracking financials
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-[#1A3C34]">Company Name *</Label>
            <Input
              id="name"
              placeholder="Acme Corporation"
              className="border-[#1A3C34]/20 focus:border-[#1A3C34]"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="company-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="industry" className="text-[#1A3C34]">Industry *</Label>
            <Select
              value={formData.industry}
              onValueChange={(value) => setFormData({ ...formData, industry: value })}
            >
              <SelectTrigger className="border-[#1A3C34]/20 focus:border-[#1A3C34]" data-testid="industry-select">
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map((industry) => (
                  <SelectItem key={industry} value={industry}>
                    {industry}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fiscal_year_end" className="text-[#1A3C34]">Fiscal Year End</Label>
              <Select
                value={formData.fiscal_year_end}
                onValueChange={(value) => setFormData({ ...formData, fiscal_year_end: value })}
              >
                <SelectTrigger className="border-[#1A3C34]/20 focus:border-[#1A3C34]" data-testid="fiscal-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency" className="text-[#1A3C34]">Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger className="border-[#1A3C34]/20 focus:border-[#1A3C34]" data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-[#1A3C34]">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Brief description of the company..."
              className="border-[#1A3C34]/20 focus:border-[#1A3C34] min-h-[80px]"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              data-testid="company-description-input"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-[#1A3C34]/20 text-[#1A3C34]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
              disabled={loading}
              data-testid="create-company-btn"
            >
              {loading ? 'Creating...' : 'Create Company'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCompanyModal;
